import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Pagination } from '@/components/shared/pagination';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { cn } from '@/utils/cn';

import { DepartmentUnitLeadershipPanel } from './department-unit-leadership-panel';
import { DepartmentUnitMembersPanel } from './department-unit-members-panel';

interface DepartmentUnit {
  id: string;
  name: string;
  description: string | null;
  officeCount: number;
  memberCount: number;
}

interface PageMeta {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

interface DepartmentUnitsCollection {
  data: DepartmentUnit[];
  page: PageMeta;
}

interface SavedDepartmentUnit {
  id: string;
  name: string;
  description: string | null;
}

const unitsListQueryKey = (departmentId: string, page: number, pageSize: number) =>
  ['people', 'departments', departmentId, 'units', 'list', page, pageSize] as const;

const unitsListQueryPrefix = (departmentId: string) =>
  ['people', 'departments', departmentId, 'units', 'list'] as const;

const mergeSavedUnit = (
  rows: DepartmentUnit[],
  saved: SavedDepartmentUnit,
  editingUnitId: string | null,
): DepartmentUnit[] => {
  if (editingUnitId) {
    return rows.map((row) =>
      row.id === saved.id
        ? { ...row, name: saved.name, description: saved.description }
        : row,
    );
  }
  return [
    ...rows,
    {
      id: saved.id,
      name: saved.name,
      description: saved.description,
      officeCount: 0,
      memberCount: 0,
    },
  ];
};

const patchUnitsCollection = (
  current: DepartmentUnitsCollection | undefined,
  saved: SavedDepartmentUnit,
  editingUnitId: string | null,
): DepartmentUnitsCollection | undefined => {
  if (!current) return current;
  const isCreate = !editingUnitId;
  return {
    ...current,
    data: mergeSavedUnit(current.data, saved, editingUnitId),
    page: isCreate
      ? {
          ...current.page,
          totalItems: current.page.totalItems + 1,
          totalPages: Math.max(
            1,
            Math.ceil((current.page.totalItems + 1) / current.page.pageSize),
          ),
        }
      : current.page,
  };
};

interface DepartmentUnitsPanelProps {
  departmentId: string;
  canManage: boolean;
  canEditMembers: boolean;
}

export const DepartmentUnitsPanel = ({
  departmentId,
  canManage,
  canEditMembers,
}: DepartmentUnitsPanelProps) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [unitOpen, setUnitOpen] = useState(false);
  const [editingUnitId, setEditingUnitId] = useState<string | null>(null);
  const [removeUnitId, setRemoveUnitId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setPage(1);
    setSelectedUnitId(null);
  }, [departmentId]);

  const units = useQuery({
    queryKey: unitsListQueryKey(departmentId, page, pageSize),
    queryFn: () =>
      api.get<DepartmentUnitsCollection>(
        `/people/departments/${departmentId}/units?page=${page}&pageSize=${pageSize}`,
      ),
    placeholderData: keepPreviousData,
  });

  const unitRows = units.data?.data ?? [];
  const pageMeta = units.data?.page;

  useEffect(() => {
    if (selectedUnitId && unitRows.some((unit) => unit.id === selectedUnitId)) return;
    if (units.isFetching && selectedUnitId) return;
    setSelectedUnitId(unitRows[0]?.id ?? null);
  }, [selectedUnitId, unitRows, units.isFetching]);

  useEffect(() => {
    if (!unitOpen) return;
    if (editingUnitId) {
      const unit = unitRows.find((row) => row.id === editingUnitId);
      if (!unit) return;
      setName(unit.name);
      setDescription(unit.description ?? '');
      return;
    }
    setName('');
    setDescription('');
  }, [unitOpen, editingUnitId, unitRows]);

  const applySavedUnit = (saved: SavedDepartmentUnit, editingUnitId: string | null) => {
    queryClient.setQueryData<DepartmentUnitsCollection>(
      unitsListQueryKey(departmentId, page, pageSize),
      (current) => patchUnitsCollection(current, saved, editingUnitId),
    );
    queryClient.setQueryData<{ data: Array<{ id: string; name: string }> }>(
      ['people', 'departments', departmentId, 'units', 'filter'],
      (current) => {
        if (!current) return current;
        if (editingUnitId) {
          return {
            ...current,
            data: current.data.map((row) =>
              row.id === saved.id ? { ...row, name: saved.name } : row,
            ),
          };
        }
        return {
          ...current,
          data: [...current.data, { id: saved.id, name: saved.name }],
        };
      },
    );
  };

  const refreshUnitSummaries = () => {
    void queryClient.refetchQueries({ queryKey: unitsListQueryPrefix(departmentId) });
    void queryClient.invalidateQueries({
      queryKey: ['people', 'departments', departmentId, 'insights'],
    });
    void queryClient.invalidateQueries({ queryKey: ['people', 'department-units'] });
    void queryClient.invalidateQueries({ queryKey: ['people', 'departments'] });
  };

  const openCreateUnit = () => {
    setEditingUnitId(null);
    setUnitOpen(true);
  };

  const openEditUnit = (unitId: string) => {
    setEditingUnitId(unitId);
    setUnitOpen(true);
  };

  const closeUnitDialog = () => {
    setUnitOpen(false);
    setEditingUnitId(null);
  };

  const saveUnit = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
      };
      if (editingUnitId) {
        return api.patch<SavedDepartmentUnit>(
          `/people/departments/${departmentId}/units/${editingUnitId}`,
          body,
        );
      }
      return api.post<SavedDepartmentUnit>(`/people/departments/${departmentId}/units`, body);
    },
    onSuccess: (unit) => {
      const editingId = editingUnitId;
      toast.success(t('departments.units.saved'));
      applySavedUnit(unit, editingId);
      closeUnitDialog();
      setSelectedUnitId(unit.id);
      refreshUnitSummaries();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const removeUnit = useMutation({
    mutationFn: (unitId: string) =>
      api.delete(`/people/departments/${departmentId}/units/${unitId}`),
    onSuccess: (_result, unitId) => {
      toast.success(t('departments.units.removed'));
      queryClient.setQueryData<DepartmentUnitsCollection>(
        unitsListQueryKey(departmentId, page, pageSize),
        (current) => {
          if (!current) return current;
          const remaining = current.data.filter((row) => row.id !== unitId);
          const totalItems = Math.max(0, current.page.totalItems - 1);
          return {
            ...current,
            data: remaining,
            page: {
              ...current.page,
              totalItems,
              totalPages: totalItems === 0 ? 0 : Math.ceil(totalItems / current.page.pageSize),
            },
          };
        },
      );
      if (unitRows.length <= 1 && page > 1) {
        setPage(page - 1);
      }
      queryClient.setQueryData<{ data: Array<{ id: string; name: string }> }>(
        ['people', 'departments', departmentId, 'units', 'filter'],
        (current) =>
          current ? { ...current, data: current.data.filter((row) => row.id !== unitId) } : current,
      );
      setRemoveUnitId(null);
      setSelectedUnitId(null);
      refreshUnitSummaries();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const selectedUnit = unitRows.find((unit) => unit.id === selectedUnitId) ?? null;
  const paginationLabels = {
    showing: ({ from, to, total }: { from: number; to: number; total: number }) =>
      tCommon('pagination.showing', { from, to, total }),
    perPage: tCommon('pagination.perPage'),
    first: tCommon('pagination.first'),
    previous: tCommon('pagination.previous'),
    next: tCommon('pagination.next'),
    last: tCommon('pagination.last'),
  };

  if (units.isPending && !units.data) {
    return <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>;
  }

  if ((pageMeta?.totalItems ?? 0) === 0 && unitRows.length === 0) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-body-sm text-fg-secondary">{t('departments.units.empty')}</p>
        {canManage ? (
          <Button size="sm" variant="outline" onClick={openCreateUnit}>
            {t('departments.units.add')}
          </Button>
        ) : null}

        <UnitDialog
          open={unitOpen}
          editingUnitId={editingUnitId}
          name={name}
          description={description}
          onNameChange={setName}
          onDescriptionChange={setDescription}
          onOpenChange={(open) => {
            if (!open) closeUnitDialog();
            else setUnitOpen(true);
          }}
          onSubmit={() => saveUnit.mutate()}
          saving={saveUnit.isPending}
          canManage={canManage}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
        <nav className="w-full shrink-0 lg:w-56" aria-label={t('departments.tabs.units')}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <span className="text-caption font-medium text-fg-secondary">
              {t('departments.tabs.units')}
            </span>
            {canManage ? (
              <Button size="sm" variant="outline" onClick={openCreateUnit}>
                {t('departments.units.add')}
              </Button>
            ) : null}
          </div>
          <ul className="flex flex-col gap-1">
            {unitRows.map((unit) => {
              const isSelected = unit.id === selectedUnitId;
              return (
                <li key={unit.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedUnitId(unit.id)}
                    className={cn(
                      'w-full rounded-lg border px-3 py-2.5 text-left transition-colors',
                      isSelected
                        ? 'border-brand bg-brand/5'
                        : 'border-border bg-surface hover:bg-surface-hover',
                    )}
                  >
                    <span className="block text-body-sm font-medium text-fg">{unit.name}</span>
                    <span className="mt-0.5 block text-caption text-fg-secondary">
                      {t('departments.units.memberCount', { count: unit.memberCount })}
                    </span>
                    {unit.description ? (
                      <span className="mt-0.5 block truncate text-caption text-fg-tertiary">
                        {unit.description}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="min-w-0 flex-1 border-t border-border pt-6 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
          {selectedUnit ? (
            <div className="flex flex-col gap-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-body font-medium text-fg">{selectedUnit.name}</h3>
                  {selectedUnit.description ? (
                    <p className="mt-1 text-body-sm text-fg-secondary">{selectedUnit.description}</p>
                  ) : null}
                </div>
                {canManage ? (
                  <div className="flex shrink-0 flex-wrap justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditUnit(selectedUnit.id)}
                    >
                      {tCommon('action.edit')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRemoveUnitId(selectedUnit.id)}
                    >
                      {tCommon('action.remove')}
                    </Button>
                  </div>
                ) : null}
              </div>

              <Tabs defaultValue="members">
                <TabsList>
                  <TabsTrigger value="members">{t('departments.units.tabs.members')}</TabsTrigger>
                  <TabsTrigger value="leadership">
                    {t('departments.units.tabs.leadership')}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="members">
                  <DepartmentUnitMembersPanel
                    departmentId={departmentId}
                    unitId={selectedUnit.id}
                    canEdit={canEditMembers}
                  />
                </TabsContent>
                <TabsContent value="leadership">
                  <DepartmentUnitLeadershipPanel
                    departmentId={departmentId}
                    unitId={selectedUnit.id}
                    canManage={canManage}
                  />
                </TabsContent>
              </Tabs>
            </div>
          ) : (
            <p className="text-body-sm text-fg-secondary">{t('departments.units.selectPrompt')}</p>
          )}
        </div>
      </div>

      {pageMeta && pageMeta.totalPages > 1 ? (
        <Pagination
          page={pageMeta.page}
          pageSize={pageMeta.pageSize}
          totalItems={pageMeta.totalItems}
          totalPages={pageMeta.totalPages}
          onPageChange={setPage}
          onPageSizeChange={(next) => {
            setPageSize(next);
            setPage(1);
          }}
          labels={paginationLabels}
        />
      ) : null}

      <UnitDialog
        open={unitOpen}
        editingUnitId={editingUnitId}
        name={name}
        description={description}
        onNameChange={setName}
        onDescriptionChange={setDescription}
        onOpenChange={(open) => {
          if (!open) closeUnitDialog();
          else setUnitOpen(true);
        }}
        onSubmit={() => saveUnit.mutate()}
        saving={saveUnit.isPending}
        canManage={canManage}
      />

      <ConfirmDialog
        open={Boolean(removeUnitId)}
        onOpenChange={(open) => {
          if (!open) setRemoveUnitId(null);
        }}
        title={t('departments.units.removeTitle')}
        description={t('departments.units.removeDescription')}
        confirmLabel={tCommon('action.remove')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={removeUnit.isPending}
        onConfirm={() => {
          if (removeUnitId) removeUnit.mutate(removeUnitId);
        }}
      />
    </div>
  );
};

interface UnitDialogProps {
  open: boolean;
  editingUnitId: string | null;
  name: string;
  description: string;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onOpenChange: (open: boolean) => void;
  onSubmit: () => void;
  saving: boolean;
  canManage: boolean;
}

const UnitDialog = ({
  open,
  editingUnitId,
  name,
  description,
  onNameChange,
  onDescriptionChange,
  onOpenChange,
  onSubmit,
  saving,
  canManage,
}: UnitDialogProps) => {
  const { t } = useTranslation('people');
  const { t: tCommon } = useTranslation();

  if (!canManage) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={tCommon('action.close')}>
        <DialogHeader>
          <DialogTitle>
            {editingUnitId ? t('departments.units.edit') : t('departments.units.add')}
          </DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (!name.trim()) return;
            onSubmit();
          }}
        >
          <DialogBody contentClassName="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="unit-name">{t('departments.units.name')}</Label>
              <Input
                id="unit-name"
                value={name}
                onChange={(event) => onNameChange(event.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="unit-description">{t('departments.units.descriptionField')}</Label>
              <Textarea
                id="unit-description"
                value={description}
                onChange={(event) => onDescriptionChange(event.target.value)}
              />
            </div>
          </DialogBody>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {tCommon('action.cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!name.trim()}>
              {t('departments.units.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
